import React from 'react';

import translateFa from 'lib/i18n/fa';

const SKIP_SELECTOR = 'code, pre, kbd, samp, script, style, textarea, .monaco-editor, [data-saba-no-translate]';
const TRANSLATED_ATTRIBUTES = [ 'aria-label', 'alt', 'placeholder', 'title' ] as const;

function shouldSkip(element: Element | null) {
  return Boolean(element?.closest(SKIP_SELECTOR));
}

function localizeElement(element: Element) {
  if (shouldSkip(element)) {
    return;
  }

  TRANSLATED_ATTRIBUTES.forEach((attribute) => {
    const value = element.getAttribute(attribute);
    if (!value) {
      return;
    }
    const translated = translateFa(value);
    if (translated !== value) {
      element.setAttribute(attribute, translated);
    }
  });
}

function localizeNode(root: Node) {
  if (root.nodeType === Node.TEXT_NODE) {
    const textNode = root as Text;
    if (shouldSkip(textNode.parentElement) || !textNode.nodeValue) {
      return;
    }
    const translated = translateFa(textNode.nodeValue);
    if (translated !== textNode.nodeValue) {
      textNode.nodeValue = translated;
    }
    return;
  }

  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) {
    return;
  }

  if (root.nodeType === Node.ELEMENT_NODE) {
    localizeElement(root as Element);
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (node.nodeType === Node.TEXT_NODE) {
      localizeNode(node);
    } else {
      localizeElement(node as Element);
    }
  }
}

const SabaLocalization = () => {
  React.useEffect(() => {
    const html = document.documentElement;
    html.lang = 'fa';
    html.dir = 'rtl';
    html.classList.remove('dark');
    html.classList.add('light');
    html.style.colorScheme = 'light';

    localizeNode(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          localizeElement(mutation.target as Element);
          return;
        }
        if (mutation.type === 'characterData') {
          localizeNode(mutation.target);
          return;
        }
        mutation.addedNodes.forEach(localizeNode);
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [ ...TRANSLATED_ATTRIBUTES ],
      characterData: true,
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  return null;
};

export default SabaLocalization;
